import { useCallback, useLayoutEffect, type RefObject } from 'react';
import { adjustTextareaHeight } from '../utils/autoResizeTextarea';

interface UseAutoResizeTextareaOptions {
  enabled?: boolean;
  minRows?: number;
  maxRows?: number;
  value?: string;
}

export function useAutoResizeTextarea(
  ref: RefObject<HTMLTextAreaElement | null>,
  {
    enabled = false,
    minRows = 1,
    maxRows = 12,
    value,
  }: UseAutoResizeTextareaOptions = {},
) {
  const adjust = useCallback(() => {
    if (!enabled) return;
    const element = ref.current;
    if (!element) return;
    adjustTextareaHeight(element, minRows, maxRows);
  }, [enabled, maxRows, minRows, ref]);

  useLayoutEffect(() => {
    if (!enabled) return;

    adjust();

    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') return undefined;

    let lastWidth = element.getBoundingClientRect().width;
    let frame = 0;

    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      // Só recalcula quando a largura muda (altura muda pelo próprio resize).
      if (Math.abs(width - lastWidth) < 0.5) return;
      lastWidth = width;
      // Adia o ajuste para o próximo frame para evitar o aviso
      // "ResizeObserver loop completed with undelivered notifications",
      // já que ajustar a altura aqui dispararia um novo ciclo de layout.
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        frame = 0;
        adjust();
      });
    });
    observer.observe(element);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [adjust, enabled, ref, value]);

  return adjust;
}
